import React, { useState, useEffect } from 'react';
import {
  Home, TrendingUp, Shield, AlertTriangle,
  ChevronRight, CheckCircle, DollarSign, Award,
  Users, Building, Zap, Clock, ArrowRight, Star,
  LineChart, Briefcase, Target, TrendingDown, Lock
} from 'lucide-react';

const CarAssetLeadPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [carIQScore, setCarIQScore] = useState(300);
  const [vehicleValue, setVehicleValue] = useState(0);
  const videoRef = React.useRef<HTMLDivElement>(null);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === 'M@ke@llth31mpactComcast!!') {
      setIsAuthenticated(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const scrollToVideo = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    setIsVisible(true);
    // Animate Car IQ Score
    const scoreInterval = setInterval(() => {
      setCarIQScore(prev => {
        if (prev >= 750) {
          clearInterval(scoreInterval);
          return 750;
        }
        return prev + 5;
      });
    }, 20);

    // Animate vehicle value
    const valueInterval = setInterval(() => {
      setVehicleValue(prev => {
        if (prev >= 34850) {
          clearInterval(valueInterval);
          return 34850;
        }
        return prev + 460;
      });
    }, 20);

    return () => {
      clearInterval(scoreInterval);
      clearInterval(valueInterval);
    };
  }, []);

  const features = [
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Professional Fleet Management",
      description: "Monitor your vehicle like fleet managers track commercial vehicles—with the same tools once available only to logistics companies",
      benefit: "Access to ASE mechanic and fleet manager strategies"
    },
    {
      icon: <LineChart className="w-6 h-6" />,
      title: "Asset Performance Analytics",
      description: "Real-time tracking of your vehicle's financial health, predicted maintenance costs, and value optimization opportunities",
      benefit: "Make data-driven decisions like the pros"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Expert-Vetted Action Plans",
      description: "Get maintenance guidance backed by automotive professionals and matched with verified mechanics when you're ready to act",
      benefit: "Professional expertise meets service provider convenience"
    }
  ];

  const stats = [
    { number: "64%", label: "of drivers lack professional guidance", source: "FinanceBuzz Survey 2024" },
    { number: "$12,297", label: "average annual vehicle ownership costs", source: "AAA 2024" },
    { number: "$1,193", label: "average cost of amateur management", source: "Utires Study 2024" },
    { number: "3-9x", label: "cost increase without expert oversight", source: "Fleet Industry Analysis" }
  ];

  const democratizationPoints = [
    {
      title: "Fleet Management Tools",
      icon: <Building className="w-8 h-8" />,
      before: "Only available to commercial fleet operators and logistics companies",
      after: "Now in your pocket for your second-largest asset",
      features: [
        "Asset performance dashboards",
        "Predictive maintenance modeling",
        "Depreciation tracking",
        "Professional-grade reporting"
      ]
    },
    {
      title: "Expert Knowledge",
      icon: <Award className="w-8 h-8" />,
      before: "Fleet management software costs $600-$1,800 per vehicle annually",
      after: "Access the same expertise for the cost of a monthly coffee",
      features: [
        "ASE mechanic insights",
        "Professional maintenance timing",
        "Value-retention recommendations",
        "Verified service provider matching"
      ]
    },
    {
      title: "Proactive Strategy",
      icon: <TrendingUp className="w-8 h-8" />,
      before: "Reactive car owners vs. strategic fleet managers",
      after: "Everyone manages their second-largest asset like a professional",
      features: [
        "Forward-looking maintenance plans",
        "Value preservation strategies",
        "Cost avoidance modeling",
        "Asset depreciation tactics"
      ]
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Required</h1>
            <p className="text-gray-600">Please enter the password to access this page</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError(false);
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  passwordError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter password"
                autoFocus
              />
              {passwordError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Incorrect password. Please try again.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Access Page
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
          <div className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-blue-800/50 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-600">
                <Briefcase className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium">Fleet Manager in Your Pocket</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Manage Your Vehicle Like
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 mt-2">
                  Professional Fleet Managers.
                </span>
              </h1>
              
              <p className="text-xl text-blue-100 leading-relaxed">
                The same tools fleet managers use for commercial vehicles are now available for your 
                second-largest asset. <span className="font-semibold text-white">64% of drivers manage 
                their vehicles without professional guidance</span>, losing $1,193 in avoidable costs. 
                We're democratizing access to expert-level vehicle management.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center">
                  Get Your Vehicle Dashboard
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={scrollToVideo} className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">
                  See How It Works
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Professional tools, simplified</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Expert guidance included</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - Vehicle Dashboard */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 shadow-2xl">
                <div className="space-y-6">
                  {/* Asset Value Display */}
                  <div className="text-center border-b border-white/20 pb-6">
                    <div className="text-sm font-medium text-blue-200 mb-2">YOUR VEHICLE PORTFOLIO</div>
                    <div className="text-4xl font-bold mb-2">${(vehicleValue).toLocaleString()}</div>
                    <div className="flex items-center justify-center space-x-2 text-green-400 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      <span>+2.1% vs. market avg depreciation</span>
                    </div>
                  </div>

                  {/* Car IQ Score Display */}
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-200 mb-4">ASSET HEALTH SCORE</div>
                    <div className="relative inline-block">
                      <svg className="transform -rotate-90 w-40 h-40">
                        <circle
                          cx="80"
                          cy="80"
                          r="72"
                          stroke="rgba(255,255,255,0.1)"
                          strokeWidth="10"
                          fill="none"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="72"
                          stroke="url(#gradient)"
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${(carIQScore - 300) / 550 * 452} 452`}
                          className="transition-all duration-300"
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#10B981" />
                            <stop offset="100%" stopColor="#3B82F6" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl font-bold">{carIQScore}</div>
                          <div className="text-xs text-blue-200">Excellent</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                      <div className="text-lg font-bold text-yellow-400">3</div>
                      <div className="text-xs text-blue-200">Priority Actions</div>
                    </div>
                    <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                      <div className="text-lg font-bold text-green-400">$2.4K</div>
                      <div className="text-xs text-blue-200">Costs Avoided</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section ref={videoRef} className="bg-gray-50 py-20">
        <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              See How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Watch how professional fleet management tools work for your vehicle
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black w-3/4 mx-auto">
            <video
              controls
              className="w-full"
              poster=""
            >
              <source src="/AiCarAssetCoach.MOV" type="video/quicktime" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="bg-white border-y border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-slate-900 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600 mb-1">{stat.label}</div>
                <div className="text-xs text-gray-400">{stat.source}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Democratization Story */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Professional Asset Management, Democratized
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Fleet managers have always had sophisticated tools to track, optimize, and protect their 
              commercial vehicles. Now you can manage your second-largest asset the same way—
              without the $600-$1,800 annual software costs or commercial credentials.
            </p>
          </div>

          <div className="space-y-12">
            {democratizationPoints.map((point, idx) => (
              <div key={idx} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="grid lg:grid-cols-2 gap-8 p-8">
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-slate-100 rounded-xl text-slate-700">
                        {point.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{point.title}</h3>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <div className="text-sm font-semibold text-red-800 mb-1">Before</div>
                        <div className="text-sm text-red-700">{point.before}</div>
                      </div>
                      
                      <div className="flex justify-center">
                        <ArrowRight className="w-6 h-6 text-gray-400" />
                      </div>
                      
                      <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
                        <div className="text-sm font-semibold text-green-800 mb-1">Now</div>
                        <div className="text-sm text-green-700">{point.after}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4">What You Get Access To:</h4>
                    <ul className="space-y-3">
                      {point.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Your Personal Fleet Management Suite
            </h2>
            <p className="text-xl text-gray-600">
              Everything fleet managers use to protect thousands of commercial vehicles—
              adapted for your car and accessible from your phone
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={`group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 cursor-pointer transition-all duration-300 ${
                  activeFeature === idx 
                    ? 'border-blue-600 shadow-2xl scale-105' 
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-xl'
                }`}
                onMouseEnter={() => setActiveFeature(idx)}
              >
                <div className={`inline-flex p-4 rounded-xl mb-6 transition-all duration-300 ${
                  activeFeature === idx 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100'
                }`}>
                  {feature.icon}
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {feature.description}
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-start">
                    <Award className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-blue-800 font-medium">{feature.benefit}</span>
                  </div>
                </div>

                <div className={`mt-6 flex items-center text-blue-600 font-semibold transition-all duration-300 ${
                  activeFeature === idx ? 'translate-x-2' : ''
                }`}>
                  Learn More
                  <ChevronRight className="w-5 h-5 ml-1" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Management Made Simple
            </h2>
            <p className="text-xl text-gray-600">
              Get started in minutes, not months
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-bold text-lg mb-2">Connect Your Vehicle</h3>
              <p className="text-gray-600 text-sm">Enter your vehicle details in 60 seconds</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-bold text-lg mb-2">Get Your Dashboard</h3>
              <p className="text-gray-600 text-sm">See your asset health score and current value</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-bold text-lg mb-2">Follow Expert Guidance</h3>
              <p className="text-gray-600 text-sm">Act on automotive professional recommendations</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="font-bold text-lg mb-2">Match with Providers</h3>
              <p className="text-gray-600 text-sm">Connect with verified mechanics when ready</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Start Managing Your Second-Largest Asset Like a Pro
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join thousands of vehicle owners who've gained access to professional-grade fleet 
            management tools. Your car deserves the same attention fleet managers give their vehicles.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-900 px-10 py-5 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center justify-center group">
              Get Your Dashboard Free
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </button>
            <button onClick={scrollToVideo} className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm">
              See Demo Video
            </button>
          </div>

          <div className="mt-8 flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <span>Professional tools for everyone</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-400" />
              <span>Expert guidance included</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-bold mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Expert Network</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Vehicle Management Guide</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Asset Optimization Tips</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Service Provider Directory</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Partners</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Automotive Professionals</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Service Providers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Enterprise</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm">© 2025 Car Asset Management Platform. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CarAssetLeadPage;
